import { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { formatINR } from '../../utils/formatters';
import { IndianRupee, AlertCircle, CheckCircle } from 'lucide-react';

interface BillingRow {
  tenantId: string;
  tenantName: string;
  slug: string;
  contractValue: number;
  billingCycle: string;
  nextDue: string | null;
  status: string;
  totalPaid: number;
  totalOutstanding: number;
}

interface BillingSummary {
  totalMRR: number;
  totalOutstanding: number;
  collectionRate: number;
}

export function BillingPage() {
  const [billing, setBilling] = useState<BillingRow[]>([]);
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/billing')
      .then(res => {
        setBilling(res.data.data.billing);
        setSummary(res.data.data.summary);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--ink-0)] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Billing Tracker</h1>
        <p className="text-sm text-gray-500">Tenant contracts and payment status</p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-600">
                <IndianRupee size={20} className="text-white" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">Total MRR</p>
                <p className="text-xl font-bold text-gray-900">{formatINR(summary.totalMRR)}</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-600">
                <AlertCircle size={20} className="text-white" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">Total Outstanding</p>
                <p className="text-xl font-bold text-gray-900">{formatINR(summary.totalOutstanding)}</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                summary.collectionRate >= 50 ? 'bg-emerald-600' : 'bg-red-600'
              }`}>
                <CheckCircle size={20} className="text-white" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">Collection Rate</p>
                <p className="text-xl font-bold text-gray-900">{summary.collectionRate}%</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Billing Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="px-4 py-3 font-medium text-gray-500">Tenant</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Contract Value</th>
              <th className="px-4 py-3 font-medium text-gray-500">Billing Cycle</th>
              <th className="px-4 py-3 font-medium text-gray-500">Next Due</th>
              <th className="px-4 py-3 font-medium text-gray-500">Status</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Total Paid</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Outstanding</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {billing.map(b => (
              <tr key={b.tenantId} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{b.tenantName}</p>
                  <p className="text-xs text-gray-400">{b.slug}</p>
                </td>
                <td className="px-4 py-3 text-right font-medium text-gray-900">{formatINR(b.contractValue)}</td>
                <td className="px-4 py-3 capitalize text-gray-600">{b.billingCycle}</td>
                <td className="px-4 py-3 text-gray-500 text-xs">{b.nextDue || '--'}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                    b.status === 'Paid' ? 'bg-emerald-100 text-emerald-700' :
                    b.status === 'Overdue' ? 'bg-red-100 text-red-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>
                    {b.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-emerald-600 font-medium">{formatINR(b.totalPaid)}</td>
                <td className="px-4 py-3 text-right text-amber-600 font-medium">{formatINR(b.totalOutstanding)}</td>
              </tr>
            ))}
            {billing.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-400">No billing data.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
